/* eslint-disable @next/next/no-img-element */
import {
  DeliveredWeeklyVenn,
  PublicProfile,
  WeeklyVennPosition,
} from "@dimensional-engineering/dimensional-models";

import styles from "./WeeklyVenn.module.css";

import { ProfileColorMap } from "@/components/shared/ColorMap";
import { RootState } from "@/redux/store";

export type VennPeerPoint = {
  authorUid: string;
  position: WeeklyVennPosition;
  imageUrl: string;
  userName: string;
  publicProfile: RootState["connections"]["publicProfiles"][number];
  createdOn: number;
};

export type VennRenderPoint = {
  key: string;
  position: WeeklyVennPosition;
  imageUrl: string;
  publicProfile: PublicProfile | null | undefined;
  isMine?: boolean;
  peerPoint?: VennPeerPoint;
};

type Props = {
  topLabel: string;
  leftLabel: string;
  rightLabel: string;
  myPoint?: DeliveredWeeklyVenn | null;
  myImageUrl?: string;
  myPublicProfile?: RootState["user"]["publicProfileData"];
  peerPoints?: VennPeerPoint[];
  showMyPoint?: boolean;
  avatarSize?: "small" | "medium";
  layout?:
    | "default"
    | "cta"
    | "modal"
    | "sharedPostBlock"
    | "sharedPostModal"
    | "socialShare";
  showLabels?: boolean;
  limitSections?: boolean;
  onMyPointClick?: () => void;
  onPeerPointClick?: (point: VennPeerPoint) => void;
};

const SECTION_LIMITS: Record<WeeklyVennPosition, number> = {
  [WeeklyVennPosition.top]: 5,
  [WeeklyVennPosition.left]: 5,
  [WeeklyVennPosition.right]: 5,
  [WeeklyVennPosition.topLeft]: 3,
  [WeeklyVennPosition.topRight]: 3,
  [WeeklyVennPosition.leftRight]: 3,
  [WeeklyVennPosition.centre]: 3,
};

const SECTION_ANCHORS: Record<
  WeeklyVennPosition,
  { x: number; y: number; spreadX: number; spreadY: number }
> = {
  [WeeklyVennPosition.top]: { x: 50, y: 22, spreadX: 22, spreadY: 7 },
  [WeeklyVennPosition.left]: { x: 19, y: 71, spreadX: 22, spreadY: 13 },
  [WeeklyVennPosition.right]: { x: 81, y: 71, spreadX: 22, spreadY: 13 },
  [WeeklyVennPosition.topLeft]: { x: 31, y: 45, spreadX: 12, spreadY: 10 },
  [WeeklyVennPosition.topRight]: { x: 69, y: 45, spreadX: 12, spreadY: 10 },
  [WeeklyVennPosition.leftRight]: { x: 50, y: 78, spreadX: 14, spreadY: 10 },
  [WeeklyVennPosition.centre]: { x: 50, y: 55, spreadX: 11, spreadY: 8 },
};

const CTA_SECTION_ANCHORS: typeof SECTION_ANCHORS = {
  [WeeklyVennPosition.top]: { x: 50, y: 31, spreadX: 24, spreadY: 8 },
  [WeeklyVennPosition.left]: { x: 17, y: 72, spreadX: 22, spreadY: 13 },
  [WeeklyVennPosition.right]: { x: 83, y: 72, spreadX: 22, spreadY: 13 },
  [WeeklyVennPosition.topLeft]: { x: 29, y: 51, spreadX: 12, spreadY: 10 },
  [WeeklyVennPosition.topRight]: { x: 71, y: 51, spreadX: 12, spreadY: 10 },
  [WeeklyVennPosition.leftRight]: { x: 50, y: 78.5, spreadX: 14, spreadY: 10 },
  [WeeklyVennPosition.centre]: { x: 50, y: 60, spreadX: 11, spreadY: 8 },
};

const MODAL_SECTION_ANCHORS: typeof SECTION_ANCHORS = {
  [WeeklyVennPosition.top]: { x: 50, y: 22, spreadX: 22, spreadY: 7 },
  [WeeklyVennPosition.left]: { x: 19, y: 71, spreadX: 22, spreadY: 13 },
  [WeeklyVennPosition.right]: { x: 81, y: 71, spreadX: 22, spreadY: 13 },
  [WeeklyVennPosition.topLeft]: { x: 31, y: 45, spreadX: 12, spreadY: 10 },
  [WeeklyVennPosition.topRight]: { x: 69, y: 45, spreadX: 12, spreadY: 10 },
  [WeeklyVennPosition.leftRight]: { x: 50, y: 78, spreadX: 14, spreadY: 10 },
  [WeeklyVennPosition.centre]: { x: 50, y: 55, spreadX: 11, spreadY: 8 },
};

const MODAL_SECTION_SLOTS: Record<
  WeeklyVennPosition,
  Array<{ x: number; y: number }>
> = {
  [WeeklyVennPosition.top]: [
    { x: 50, y: 15 },
    { x: 39, y: 17 },
    { x: 61, y: 17},
    { x: 44, y: 27 },
    { x: 56, y: 27 },
  ],
  [WeeklyVennPosition.left]: [
    { x: 15, y: 53 },
    { x: 12, y: 66 },
    { x: 24, y: 68 },
    { x: 17, y: 79 },
    { x: 31, y: 85 },
  ],
  [WeeklyVennPosition.right]: [
    { x: 85, y: 53 },
    { x: 88, y: 66 },
    { x: 76, y: 68 },
    { x: 83, y: 79 },
    { x: 69, y: 85 },
  ],
  [WeeklyVennPosition.topLeft]: [
    { x: 25, y: 43 },
    { x: 30, y: 53 },
    { x: 36, y: 42 },
  ],
  [WeeklyVennPosition.topRight]: [
    { x: 75, y: 43 },
    { x: 70, y: 53 },
    { x: 64, y: 42 },
  ],
  [WeeklyVennPosition.leftRight]: [
    { x: 43, y: 74 },
    { x: 57, y: 74 },
    { x: 50, y: 82 },
  ],
  [WeeklyVennPosition.centre]: [
    { x: 50, y: 47 },
    { x: 44, y: 57 },
    { x: 56, y: 57 },
  ],
};

const SHARED_POST_BLOCK_SECTION_ANCHORS: typeof SECTION_ANCHORS = {
  [WeeklyVennPosition.top]: { x: 50, y: 24, spreadX: 22, spreadY: 7 },
  [WeeklyVennPosition.left]: { x: 22, y: 72, spreadX: 22, spreadY: 13 },
  [WeeklyVennPosition.right]: { x: 78, y: 72, spreadX: 22, spreadY: 13 },
  [WeeklyVennPosition.topLeft]: { x: 32, y: 47, spreadX: 12, spreadY: 10 },
  [WeeklyVennPosition.topRight]: { x: 68, y: 47, spreadX: 12, spreadY: 10 },
  [WeeklyVennPosition.leftRight]: { x: 50, y: 81, spreadX: 14, spreadY: 10 },
  [WeeklyVennPosition.centre]: { x: 50, y: 55, spreadX: 11, spreadY: 8 },
};

const SHARED_POST_MODAL_SECTION_ANCHORS: typeof SECTION_ANCHORS = {
  [WeeklyVennPosition.top]: { x: 50, y: 24, spreadX: 22, spreadY: 7 },
  [WeeklyVennPosition.left]: { x: 23, y: 70, spreadX: 22, spreadY: 13 },
  [WeeklyVennPosition.right]: { x: 77, y: 70, spreadX: 22, spreadY: 13 },
  [WeeklyVennPosition.topLeft]: { x: 32, y: 45, spreadX: 12, spreadY: 10 },
  [WeeklyVennPosition.topRight]: { x: 68, y: 45, spreadX: 12, spreadY: 10 },
  [WeeklyVennPosition.leftRight]: { x: 50, y: 76, spreadX: 14, spreadY: 10 },
  [WeeklyVennPosition.centre]: { x: 50, y: 55, spreadX: 11, spreadY: 8 },
};

const SOCIAL_SHARE_SECTION_ANCHORS: typeof SECTION_ANCHORS = {
  [WeeklyVennPosition.top]: { x: 50, y: 22, spreadX: 22, spreadY: 7 },
  [WeeklyVennPosition.left]: { x: 22, y: 69, spreadX: 22, spreadY: 13 },
  [WeeklyVennPosition.right]: { x: 78, y: 69, spreadX: 22, spreadY: 13 },
  [WeeklyVennPosition.topLeft]: { x: 32, y: 46, spreadX: 12, spreadY: 10 },
  [WeeklyVennPosition.topRight]: { x: 68, y: 46, spreadX: 12, spreadY: 10 },
  [WeeklyVennPosition.leftRight]: { x: 50, y: 76, spreadX: 14, spreadY: 10 },
  [WeeklyVennPosition.centre]: { x: 50, y: 55, spreadX: 11, spreadY: 8 },
};

export default function WeeklyVennDiagram(props: Props) {
  const points = limitVennPoints({
    myPoint: props.myPoint,
    myImageUrl: props.myImageUrl,
    myPublicProfile: props.myPublicProfile,
    peerPoints: props.peerPoints ?? [],
    showMyPoint: props.showMyPoint ?? true,
    limitSections: props.limitSections ?? false,
  });

  return (
    <div className={styles.vennDiagram}>
      <div className={styles.vennCircleTop}></div>
      <div className={styles.vennCircleLeft}></div>
      <div className={styles.vennCircleRight}></div>

      {(props.showLabels ?? true) && (
        <>
          <p className={styles.vennTopLabel}>
            {capitalizeFirstLetterOnly(props.topLabel)}
          </p>
          <p className={styles.vennLeftLabel}>
            {capitalizeFirstLetterOnly(props.leftLabel)}
          </p>
          <p className={styles.vennRightLabel}>
            {capitalizeFirstLetterOnly(props.rightLabel)}
          </p>
        </>
      )}

      {points.map((point, index) => {
        const sectionPoints = points.filter((p) => p.position === point.position);
        const sectionIndex = sectionPoints.findIndex((p) => p.key === point.key);
        const coordinates = getPointCoordinates(
          point.position,
          sectionIndex,
          sectionPoints.length,
          props.layout
        );

        return (
          <button
            type="button"
            key={`${point.key}-${index}`}
            className={styles.vennAvatarButton}
            style={{
              left: `${coordinates.x}%`,
              top: `${coordinates.y}%`,
            }}
            onClick={(event) => {
              event.stopPropagation();
              if (point.isMine) {
                props.onMyPointClick?.();
                return;
              }
              if (point.peerPoint) {
                props.onPeerPointClick?.(point.peerPoint);
              }
            }}
          >
            <VennAvatar
              imageUrl={point.imageUrl}
              publicProfile={point.publicProfile}
              size={props.avatarSize ?? "small"}
            />
          </button>
        );
      })}
    </div>
  );
}

export function getVennPositionLabel(position: WeeklyVennPosition) {
  switch (position) {
    case WeeklyVennPosition.top:
      return "Top";
    case WeeklyVennPosition.left:
      return "Left";
    case WeeklyVennPosition.right:
      return "Right";
    case WeeklyVennPosition.topLeft:
      return "Top + left";
    case WeeklyVennPosition.topRight:
      return "Top + right";
    case WeeklyVennPosition.leftRight:
      return "Left + right";
    case WeeklyVennPosition.centre:
      return "Center";
    default:
      return "Venn";
  }
}

function limitVennPoints(args: {
  myPoint?: DeliveredWeeklyVenn | null;
  myImageUrl?: string;
  myPublicProfile?: RootState["user"]["publicProfileData"];
  peerPoints: VennPeerPoint[];
  showMyPoint: boolean;
  limitSections: boolean;
}): VennRenderPoint[] {
  const grouped = new Map<WeeklyVennPosition, VennRenderPoint[]>();

  const addPoint = (point: VennRenderPoint) => {
    const existing = grouped.get(point.position) ?? [];
    grouped.set(point.position, [...existing, point]);
  };

  if (args.myPoint && args.showMyPoint) {
    addPoint({
      key: "me",
      position: args.myPoint.position,
      imageUrl: args.myImageUrl || "/default-image.png",
      publicProfile: args.myPublicProfile,
      isMine: true,
    });
  }

  [...args.peerPoints]
    .sort((a, b) => b.createdOn - a.createdOn)
    .forEach((point) => {
      addPoint({
        key: point.authorUid,
        position: point.position,
        imageUrl: point.imageUrl || "/default-image.png",
        publicProfile: point.publicProfile,
        peerPoint: point,
      });
    });

  return (Object.values(WeeklyVennPosition) as WeeklyVennPosition[]).flatMap((position) => {
    const points = grouped.get(position) ?? [];
    if (!args.limitSections) {
      return points;
    }

    const limit = SECTION_LIMITS[position];
    const mine = points.find((point) => point.isMine);
    const peers = points.filter((point) => !point.isMine);
    if (!mine) {
      return peers.slice(0, limit);
    }
    return [mine, ...peers.slice(0, Math.max(limit - 1, 0))];
  });
}

function getPointCoordinates(
  position: WeeklyVennPosition,
  index: number,
  count: number,
  layout: Props["layout"] = "default"
) {
  const anchor =
    layout === "cta"
      ? CTA_SECTION_ANCHORS[position]
      : layout === "modal"
      ? MODAL_SECTION_ANCHORS[position]
      : layout === "sharedPostBlock"
      ? SHARED_POST_BLOCK_SECTION_ANCHORS[position]
      : layout === "sharedPostModal"
      ? SHARED_POST_MODAL_SECTION_ANCHORS[position]
      : layout === "socialShare"
      ? SOCIAL_SHARE_SECTION_ANCHORS[position]
      : SECTION_ANCHORS[position];
  if (count <= 1) {
    return { x: anchor.x, y: anchor.y };
  }

  if (layout === "modal") {
    return MODAL_SECTION_SLOTS[position][index] ?? { x: anchor.x, y: anchor.y };
  }

  const angle = (index / count) * Math.PI * 2 - Math.PI / 2;
  return {
    x: anchor.x + Math.cos(angle) * anchor.spreadX * 0.5,
    y: anchor.y + Math.sin(angle) * anchor.spreadY * 0.5,
  };
}

function VennAvatar(props: {
  imageUrl: string;
  publicProfile: PublicProfile | null | undefined;
  size: "small" | "medium";
}) {
  const borderColor = props.publicProfile?.primaryNature?.nature
    ? ProfileColorMap[props.publicProfile.primaryNature.nature]
    : "#55504F";

  return (
    <span
      className={
        props.size === "medium"
          ? styles.vennAvatarMedium
          : styles.vennAvatarSmall
      }
      style={{ borderColor }}
    >
      <img alt="" src={props.imageUrl} />
    </span>
  );
}

function capitalizeFirstLetterOnly(label: string) {
  if (!label) {
    return label;
  }

  return `${label.charAt(0).toUpperCase()}${label.slice(1).toLowerCase()}`;
}
